import React, { useEffect, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { Flex, Typography, theme } from 'antd';

import { readGscConnectionState } from '../GscConnectionState';
import { useGetGscConnectionStatusQuery } from '../api/gscConnectionApi';
import { INTEGRATIONS_ROUTE } from '../SearchPerformanceRoutes';
import useTrackSearchPerformanceEvent from '../useTrackSearchPerformanceEvent';
import GscManageDomains from './GscManageDomains';
import GscManageDomainsSkeleton from './GscManageDomainsSkeleton';
import GscSettingsReconnectNeeded from './GscSettingsReconnectNeeded';

const STRINGS = {
  title: 'GSC settings',
  subtitle: 'Add or remove domains you sync with Google Search Console.',
};

/**
 * Resolves the connection before mounting the picker: the hook seeds once on
 * first session load and cannot be told about `syncedUrls` later.
 */
function GscSettings() {
  const { token } = theme.useToken();
  const trackEvent = useTrackSearchPerformanceEvent();
  useEffect(() => {
    trackEvent('Search Performance: Settings Viewed');
  }, [trackEvent]);
  const { data: status, isLoading } = useGetGscConnectionStatusQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const connectedAccountEmail = status?.connectedAccountEmail ?? null;
  const { domains, liveDomains, isBroken, lostUrls } = readGscConnectionState(
    status?.sources ?? [],
    connectedAccountEmail
  );

  // Latch: disconnect invalidates status, so a last-domain save briefly looks
  // like "not connected" and would unmount the page mid-flight.
  const hasSeenConnectionRef = useRef(false);
  if (domains.length > 0) {
    hasSeenConnectionRef.current = true;
  }

  function renderBody() {
    if (isLoading) {
      return <GscManageDomainsSkeleton />;
    }
    // Failed status looks the same as "no domains" — send them to the card,
    // which is where the error is shown.
    if (!hasSeenConnectionRef.current && domains.length === 0) {
      return <Navigate to={INTEGRATIONS_ROUTE} replace />;
    }
    if (isBroken) {
      return <GscSettingsReconnectNeeded />;
    }
    return <GscManageDomains syncedUrls={liveDomains} lostUrls={lostUrls} connectedAccountEmail={connectedAccountEmail} />;
  }

  return (
    <Flex vertical gap={token.marginLG}>
      <Flex vertical gap={token.marginXS}>
        <Typography.Title level={2} style={{ margin: 0 }}>
          {STRINGS.title}
        </Typography.Title>
        <Typography.Text type="secondary">{STRINGS.subtitle}</Typography.Text>
      </Flex>

      {renderBody()}
    </Flex>
  );
}

export default GscSettings;
